import { ref, watch, nextTick, onBeforeUnmount, type Ref, type WatchSource } from 'vue'

const SCROLL_THRESHOLD = 20

interface ScrollHintReturn {
	showUpHint: Ref<boolean>
	showDownHint: Ref<boolean>
	upArrowTop: Ref<number>
	downArrowBottom: Ref<number>
	scrollUp: () => void
	scrollDown: () => void
}

/**
 * Composable that detects whether a scroll container has overflow and exposes
 * reactive flags for showing up / down scroll indicators.
 *
 * @param containerRef - Template ref pointing to the element (or Vue component) that
 *                       *contains* the scroll element. The composable will querySelector
 *                       inside it.
 * @param scrollSelector - CSS selector to locate the actual scrollable element within
 *                         the container (e.g. `.k-grid-content`).
 * @param trigger - Optional reactive source. When it changes the composable re-queries
 *                  the DOM for the scroll element (useful when the element is rendered
 *                  asynchronously, e.g. after data loads into a grid).
 */
export function useScrollHint(
	containerRef: Ref<any>,
	scrollSelector: string,
	trigger?: WatchSource
): ScrollHintReturn {
	const showUpHint = ref(false)
	const showDownHint = ref(false)
	const upArrowTop = ref(0)
	const downArrowBottom = ref(0)

	let scrollEl: Element | null = null
	let containerEl: Element | null = null
	let resizeObs: ResizeObserver | null = null

	function measure() {
		if (!scrollEl || !containerEl) return
		const containerRect = containerEl.getBoundingClientRect()
		const scrollRect = scrollEl.getBoundingClientRect()
		const offset = 10
		upArrowTop.value = scrollRect.top - containerRect.top + offset
		downArrowBottom.value = containerRect.bottom - scrollRect.bottom + offset
	}

	function check() {
		if (!scrollEl) {
			showUpHint.value = false
			showDownHint.value = false
			return
		}

		// Positions may change any time layout shifts (window resize, card-body padding,
		// total-row wrapping, etc.), so recompute on every check — not only on attach.
		measure()

		const hasOverflow = scrollEl.scrollHeight > scrollEl.clientHeight + 1
		const nearTop = scrollEl.scrollTop < SCROLL_THRESHOLD
		const nearBottom = scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight < SCROLL_THRESHOLD

		showUpHint.value = hasOverflow && !nearTop
		showDownHint.value = hasOverflow && !nearBottom
	}

	function attach() {
		detach()

		const root = (containerRef.value as any)?.$el ?? containerRef.value
		containerEl = root ?? null
		scrollEl = root?.querySelector(scrollSelector) ?? null

		if (!scrollEl) {
			showUpHint.value = false
			showDownHint.value = false
			return
		}

		scrollEl.addEventListener('scroll', check, { passive: true })
		// Observe both the scroll element and the container. Layout changes below the
		// scroll area (e.g. a total row wrapping on window resize) do not change the
		// scroll element's size, so observing it alone would miss them.
		resizeObs = new ResizeObserver(check)
		resizeObs.observe(scrollEl)
		if (containerEl) resizeObs.observe(containerEl)
		window.addEventListener('resize', check, { passive: true })
		check()
	}

	function detach() {
		if (scrollEl) scrollEl.removeEventListener('scroll', check)
		window.removeEventListener('resize', check)
		resizeObs?.disconnect()
		scrollEl = null
		containerEl = null
		resizeObs = null
		showUpHint.value = false
		showDownHint.value = false
	}

	function scrollUp() {
		scrollEl?.scrollBy({ top: -200, behavior: 'smooth' })
	}

	function scrollDown() {
		scrollEl?.scrollBy({ top: 200, behavior: 'smooth' })
	}

	// Re-attach when the trigger changes (DOM may have been rebuilt)
	const sources: WatchSource[] = [containerRef]
	if (trigger) sources.push(trigger)

	watch(sources, () => {
		nextTick(() => { attach() })
	})

	onBeforeUnmount(detach)

	return { showUpHint, showDownHint, upArrowTop, downArrowBottom, scrollUp, scrollDown }
}
